import {
  DARK_THEMES,
  LOGO_HEIGHTS,
  LOGO_MAX_WIDTH,
  siteThemeById,
  type LogoInfo,
  type LogoTone,
  type SiteDesign,
} from "./siteThemes";

const SAMPLE_WIDTH = 64;

/** What we suggest before a logo is uploaded (and when it's fine). */
export const LOGO_RECOMMENDATION =
  "Best as an SVG, or a PNG with a transparent background, at least 300px wide. Wide logos suit the header; trim any empty space around yours.";

/**
 * Reads a logo before it's uploaded: whether its marks are mostly dark or
 * light (transparent pixels don't count), its size, and whether it fills its
 * box edge to edge (a solid background shows as a block in the header).
 */
export async function inspectLogo(
  file: File,
): Promise<{ tone: LogoTone | null; info: LogoInfo | null }> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const vector = file.type === "image/svg+xml";
    const info = (solid: boolean): LogoInfo => ({
      width: image.naturalWidth,
      height: image.naturalHeight,
      vector,
      solid,
    });
    const width = SAMPLE_WIDTH;
    const height = Math.max(1, Math.round((image.naturalHeight / image.naturalWidth) * width) || width);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return { tone: null, info: info(false) };
    context.drawImage(image, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    let light = 0;
    let weight = 0;
    let clear = 0;
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3] / 255;
      if (alpha < 0.1) {
        clear++;
        continue;
      }
      const luminance = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
      light += luminance * alpha;
      weight += alpha;
    }
    // Hardly a see-through pixel: the logo sits on a background of its own.
    const solid = clear / (width * height) < 0.02;
    if (!weight) return { tone: null, info: info(solid) };
    const average = light / weight;
    return {
      tone: average < 0.4 ? "dark" : average > 0.7 ? "light" : "mixed",
      info: info(solid),
    };
  } catch {
    return { tone: null, info: null };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A dark logo on a dark theme, or a light one on a light theme. */
export const logoClashes = ({ theme, logo, logoTone }: SiteDesign) =>
  Boolean(logo) &&
  (DARK_THEMES.includes(theme) ? logoTone === "dark" : logoTone === "light");

/**
 * The one thing most worth fixing about the uploaded logo, if anything: it
 * clashes with the theme, it's too small to stay sharp at its size, it sits
 * on a solid box, or it's so wide it shows smaller than its size.
 */
export function logoAdvice(design: SiteDesign): string | null {
  if (!design.logo) return null;
  if (logoClashes(design))
    return `This logo is ${design.logoTone} and may be hard to see on the ${siteThemeById(design.theme).name} theme. A ${design.logoTone === "dark" ? "light" : "dark"} version will read better.`;
  const info = design.logoInfo;
  if (!info) return null;
  const height = LOGO_HEIGHTS[design.logoSize];
  // Sharp screens show two pixels for every one.
  if (!info.vector && info.height < height * 2)
    return `This logo is small (${info.width} × ${info.height}px), so it may look blurry. Use one at least ${height * 2}px tall, or an SVG.`;
  if (info.solid)
    return "This logo has a solid background, so it shows as a box. A PNG with a transparent background blends into your site.";
  if (info.width / info.height > LOGO_MAX_WIDTH / height)
    return "This logo is very wide, so it shows smaller than its size. A more compact version will read better.";
  return null;
}
